import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Sparkles, Copy, ArrowRight, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

/**
 * DirectChatModal - Direct messaging + SkillBot assist
 *
 * Replaces the old ConnectionChatModal with real message persistence.
 * Users can send direct messages AND toggle SkillBot for AI assistance.
 * SkillBot suggestions persist until user acts (Copy/Send/Discard).
 */
// Entity-specific SkillBot placeholders
const SKILLBOT_PLACEHOLDERS = {
  individual: 'E.g., Help me introduce myself and my skills...',
  organization: 'E.g., Help me introduce our company and job openings...',
  university: 'E.g., Help me introduce our university and courses...',
  institute: 'E.g., Help me introduce our institute and programs...'
};

const DirectChatModal = ({ connection, isOpen, onClose }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [skillBotMode, setSkillBotMode] = useState(false);
  const [starters, setStarters] = useState([]);
  // SkillBot suggestion state - persists until user acts
  const [skillBotSuggestion, setSkillBotSuggestion] = useState('');
  const [skillBotPrompt, setSkillBotPrompt] = useState('');
  const [skillBotLoading, setSkillBotLoading] = useState(false);
  const [isAIAssisted, setIsAIAssisted] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const currentUserId = String(user?.id || user?._id || user?.userId || '');
  const currentUserName = user?.displayName || user?.fullName || user?.name || 'You';
  const displayName = connection?.connected_user_name || 'Connection';

  // Load messages on open
  useEffect(() => {
    if (isOpen && connection?.id) {
      loadMessages();
      pollRef.current = setInterval(loadMessages, 5000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [isOpen, connection?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadMessages = useCallback(async () => {
    try {
      const res = await api.get(`/messages/${connection.id}`);
      const data = res?.data || res;
      if (data.success) {
        setMessages(data.messages || []);
        api.put(`/messages/${connection.id}/read`).catch(() => {});
      }
    } catch (err) {
      console.error('Error loading messages:', err);
    } finally {
      setInitialLoading(false);
    }
  }, [connection?.id]);

  // Load SkillBot context when toggled
  useEffect(() => {
    if (skillBotMode && starters.length === 0) {
      loadSkillBotContext();
    }
  }, [skillBotMode]);

  const loadSkillBotContext = async () => {
    try {
      const res = await api.get(`/connection-chat/context/${connection.id}`);
      const data = res?.data || res;
      if (data.success) {
        setStarters(data.conversationStarters || []);
      }
    } catch (err) {
      console.error('Error loading SkillBot context:', err);
    }
  };

  // Ask SkillBot for help - stores suggestion, does NOT add to chat
  const askSkillBot = async (text = skillBotPrompt) => {
    if (!text.trim() || skillBotLoading) return;
    setSkillBotLoading(true);
    setSkillBotPrompt('');

    try {
      const res = await api.post('/connection-chat/message', {
        connectionId: connection.id,
        message: text,
        conversationHistory: messages.slice(-10).map(m => ({
          role: m.sender_id === currentUserId ? 'user' : 'assistant',
          content: m.content
        }))
      });
      const data = res?.data || res;
      if (data.success && data.response) {
        setSkillBotSuggestion(data.response);
      } else {
        setSkillBotSuggestion('SkillBot could not generate a response. Try rephrasing your request.');
      }
    } catch (err) {
      console.error('SkillBot error:', err);
      setSkillBotSuggestion('SkillBot is temporarily unavailable. Please try again.');
    }

    setSkillBotLoading(false);
  };

  // Copy suggestion to main input field
  const useSuggestion = () => {
    setInput(skillBotSuggestion);
    setIsAIAssisted(true);
    setSkillBotSuggestion('');
    setSkillBotMode(false);
  };

  // Send suggestion directly as a message
  const sendSuggestionDirectly = async () => {
    if (!skillBotSuggestion) return;
    setLoading(true);

    const tempMsg = {
      id: 'temp-' + Date.now(),
      connection_id: connection.id,
      sender_id: currentUserId,
      sender_name: user?.displayName || user?.name || 'You',
      message_type: 'text',
      content: skillBotSuggestion,
      ai_assisted: true,
      is_read: false,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMsg]);
    setSkillBotSuggestion('');

    try {
      const res = await api.post(`/messages/${connection.id}`, { content: tempMsg.content, aiAssisted: true });
      const data = res?.data || res;
      if (data.success) {
        setMessages(prev => prev.map(m =>
          m.id === tempMsg.id ? { ...data.message, sender_name: tempMsg.sender_name } : m
        ));
      }
    } catch (err) {
      console.error('Send error:', err);
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
      const errData = err?.response?.data;
      if (errData?.error === 'CONTENT_POLICY_VIOLATION' || errData?.message?.includes?.('policy')) {
        setMessages(prev => [...prev, {
          id: 'policy-' + Date.now(),
          sender_id: 'system',
          is_system: true,
          type: 'policy_warning',
          content: errData.message || 'Your message violates platform policies. Please rephrase.',
          created_at: new Date().toISOString()
        }]);
      }
    }

    setLoading(false);
  };

  // Send direct message (non-SkillBot)
  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const text = input;
    const wasAIAssisted = isAIAssisted;
    setInput('');
    setIsAIAssisted(false);
    setLoading(true);

    const tempMsg = {
      id: 'temp-' + Date.now(),
      connection_id: connection.id,
      sender_id: currentUserId,
      sender_name: user?.displayName || user?.name || 'You',
      message_type: 'text',
      content: text,
      ai_assisted: wasAIAssisted,
      is_read: false,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMsg]);

    try {
      const res = await api.post(`/messages/${connection.id}`, { content: text, aiAssisted: wasAIAssisted });
      const data = res?.data || res;
      if (data.success) {
        setMessages(prev => prev.map(m =>
          m.id === tempMsg.id ? { ...data.message, sender_name: tempMsg.sender_name } : m
        ));
      }
    } catch (err) {
      console.error('Send error:', err);
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id));
      const errData = err?.response?.data;
      if (errData?.error === 'CONTENT_POLICY_VIOLATION' || errData?.message?.includes?.('policy')) {
        setMessages(prev => [...prev, {
          id: 'policy-' + Date.now(),
          sender_id: 'system',
          is_system: true,
          type: 'policy_warning',
          content: errData.message || 'Your message violates platform policies. Please rephrase.',
          created_at: new Date().toISOString()
        }]);
        setInput(text);
      }
    }

    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 50, padding: '16px'
    }}>
      <div style={{
        background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '640px',
        height: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
      }}>
        {/* Header */}
        <div style={{
          background: skillBotMode
            ? 'linear-gradient(135deg, #8b5cf6, #6366f1)'
            : 'linear-gradient(135deg, #06b6d4, #3b82f6)',
          padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          transition: 'background 0.3s'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px', height: '40px', background: 'rgba(255,255,255,0.2)',
              borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '18px', fontWeight: 700, color: '#fff'
            }}>
              {skillBotMode ? <Sparkles size={20} /> : (displayName[0] || 'C')}
            </div>
            <div style={{ color: '#fff' }}>
              <h3 style={{ fontWeight: 600, margin: 0 }}>
                {skillBotMode ? 'SkillBot Assist' : displayName}
              </h3>
              <p style={{ fontSize: '13px', opacity: 0.8, margin: 0 }}>
                {skillBotMode
                  ? `Helping with ${connection?.connected_user_name || 'connection'}`
                  : (connection?.connected_user_entity_type || 'connection')}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => setSkillBotMode(!skillBotMode)}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '6px 12px',
                background: skillBotMode ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '8px', color: '#fff',
                fontSize: '13px', cursor: 'pointer'
              }}
              title={skillBotMode ? 'Switch to direct messaging' : 'Get SkillBot help'}
            >
              <Sparkles size={14} />
              {skillBotMode ? 'Direct Chat' : 'SkillBot'}
            </button>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)',
              cursor: 'pointer', padding: '4px'
            }}>
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {initialLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <div style={{ color: '#9ca3af', fontSize: '14px' }}>Loading messages...</div>
            </div>
          ) : messages.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: '8px' }}>
              <p style={{ color: '#9ca3af', fontSize: '14px' }}>No messages yet. Start the conversation!</p>
              {!skillBotMode && (
                <button
                  onClick={() => setSkillBotMode(true)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    padding: '8px 16px', background: '#f3f4f6', border: 'none',
                    borderRadius: '20px', color: '#6366f1', fontSize: '13px', cursor: 'pointer'
                  }}
                >
                  <Sparkles size={14} /> Ask SkillBot to help draft a message
                </button>
              )}
            </div>
          ) : (
            messages.map((msg) => {
              // Policy warning / system message
              if (msg.is_system || msg.type === 'policy_warning') {
                return (
                  <div key={msg.id} style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }}>
                    <div style={{
                      maxWidth: '90%', background: '#fef2f2', border: '1px solid #fecaca',
                      borderRadius: '12px', padding: '12px 16px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                    }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #fecaca'
                      }}>
                        <div style={{
                          width: '28px', height: '28px', borderRadius: '50%',
                          background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          <span style={{ fontSize: '14px' }}>⚠</span>
                        </div>
                        <span style={{ fontWeight: 600, fontSize: '13px', color: '#b91c1c' }}>Policy Notice</span>
                      </div>
                      <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        {msg.content}
                      </p>
                    </div>
                  </div>
                );
              }

              const isMine = String(msg.sender_id) === currentUserId;
              const isBot = msg.sender_id === 'skillbot' || msg.message_type === 'skillbot';
              const senderLabel = isBot ? 'SkillBot' : isMine ? currentUserName : (msg.sender_name || displayName);
              return (
                <div key={msg.id} style={{
                  display: 'flex',
                  justifyContent: isMine ? 'flex-end' : 'flex-start'
                }}>
                  <div style={{
                    maxWidth: '80%', padding: '10px 14px', borderRadius: '16px',
                    ...(isBot
                      ? { background: '#ede9fe', color: '#4c1d95', borderBottomLeftRadius: '4px' }
                      : isMine
                        ? { background: '#06b6d4', color: '#fff', borderBottomRightRadius: '4px' }
                        : { background: '#f3f4f6', color: '#1f2937', borderBottomLeftRadius: '4px' })
                  }}>
                    <p style={{ fontSize: '11px', fontWeight: 600, margin: '0 0 4px', opacity: 0.7 }}>
                      {senderLabel}
                    </p>
                    <p style={{ whiteSpace: 'pre-wrap', fontSize: '14px', margin: 0, lineHeight: 1.5 }}>
                      {msg.content}
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '4px' }}>
                      <span style={{ fontSize: '10px', opacity: 0.5 }}>
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {msg.ai_assisted && (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                          padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: 500,
                          background: isMine
                            ? 'rgba(255,255,255,0.2)'
                            : 'linear-gradient(135deg, #06b6d4, #14b8a6)',
                          color: isMine ? 'rgba(255,255,255,0.9)' : '#fff'
                        }}>
                          <Sparkles size={10} /> SkillBot
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}

          {(loading || skillBotLoading) && (
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <div style={{ background: skillBotMode ? '#ede9fe' : '#f3f4f6', padding: '10px 14px', borderRadius: '16px', borderBottomLeftRadius: '4px' }}>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <span style={{ width: '8px', height: '8px', background: '#9ca3af', borderRadius: '50%', animation: 'bounce 1s infinite' }} />
                  <span style={{ width: '8px', height: '8px', background: '#9ca3af', borderRadius: '50%', animation: 'bounce 1s infinite 0.1s' }} />
                  <span style={{ width: '8px', height: '8px', background: '#9ca3af', borderRadius: '50%', animation: 'bounce 1s infinite 0.2s' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* SkillBot Suggestion Panel - PERSISTS until user acts */}
        {skillBotSuggestion && (
          <div style={{
            borderTop: '1px solid #e5e7eb',
            background: 'linear-gradient(135deg, #f5f3ff, #eff6ff)',
            padding: '12px 16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Sparkles size={14} style={{ color: '#8b5cf6' }} />
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#6d28d9' }}>SkillBot Suggestion</span>
              </div>
            </div>
            <div style={{
              background: '#fff', padding: '10px 12px', borderRadius: '8px',
              border: '1px solid #c4b5fd', fontSize: '14px', color: '#374151',
              whiteSpace: 'pre-wrap', lineHeight: 1.5, maxHeight: '120px', overflowY: 'auto',
              marginBottom: '10px'
            }}>
              {skillBotSuggestion}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={useSuggestion}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  padding: '6px 14px', fontSize: '13px',
                  background: '#fff', color: '#6d28d9',
                  border: '1px solid #c4b5fd', borderRadius: '8px', cursor: 'pointer'
                }}
              >
                <Copy size={14} /> Copy to Input
              </button>
              <button
                onClick={sendSuggestionDirectly}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  padding: '6px 14px', fontSize: '13px',
                  background: '#7c3aed', color: '#fff',
                  border: 'none', borderRadius: '8px', cursor: 'pointer'
                }}
              >
                <ArrowRight size={14} /> Send Now
              </button>
              <button
                onClick={() => setSkillBotSuggestion('')}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  padding: '6px 14px', fontSize: '13px',
                  background: 'none', color: '#9ca3af',
                  border: 'none', borderRadius: '8px', cursor: 'pointer'
                }}
              >
                <Trash2 size={14} /> Discard
              </button>
            </div>
          </div>
        )}

        {/* SkillBot Starters - shown when in SkillBot mode with few messages and no active suggestion */}
        {skillBotMode && !skillBotSuggestion && messages.length <= 1 && starters.length > 0 && (
          <div style={{ padding: '0 16px 8px' }}>
            <p style={{ fontSize: '12px', color: '#8b5cf6', marginBottom: '8px' }}>SkillBot can help:</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {starters.map((s, i) => (
                <button
                  key={i}
                  onClick={() => askSkillBot(s)}
                  style={{
                    fontSize: '12px', padding: '6px 12px', background: '#ede9fe',
                    border: 'none', borderRadius: '20px', color: '#6366f1', cursor: 'pointer'
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb' }}>
          {skillBotMode ? (
            /* SkillBot prompt input */
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={skillBotPrompt}
                onChange={(e) => setSkillBotPrompt(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && askSkillBot()}
                placeholder={SKILLBOT_PLACEHOLDERS[user?.entityType] || SKILLBOT_PLACEHOLDERS.individual}
                disabled={skillBotLoading}
                style={{
                  flex: 1, padding: '12px 16px', border: '1px solid #c4b5fd',
                  borderRadius: '12px', outline: 'none', fontSize: '14px',
                  background: '#faf5ff'
                }}
              />
              <button
                onClick={() => askSkillBot()}
                disabled={skillBotLoading || !skillBotPrompt.trim()}
                style={{
                  padding: '12px 16px',
                  background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                  color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer',
                  opacity: (skillBotLoading || !skillBotPrompt.trim()) ? 0.5 : 1,
                  display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px'
                }}
              >
                <Sparkles size={16} /> Ask
              </button>
            </div>
          ) : (
            /* Direct message input */
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                placeholder={`Message ${displayName}...`}
                disabled={loading}
                style={{
                  flex: 1, padding: '12px 16px', border: '1px solid #d1d5db',
                  borderRadius: '12px', outline: 'none', fontSize: '14px'
                }}
              />
              <button
                onClick={sendMessage}
                disabled={loading || !input.trim()}
                style={{
                  padding: '12px 16px',
                  background: 'linear-gradient(135deg, #06b6d4, #3b82f6)',
                  color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer',
                  opacity: (loading || !input.trim()) ? 0.5 : 1
                }}
              >
                <Send size={20} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DirectChatModal;
